import { Combobox } from "@headlessui/react";
import { type ComponentPropsWithoutRef, type ReactNode } from "react";
import PillarIcon from "shared/components/icons/PillarIcon";
import {
  PILLAR_ICON_NAMES,
  type PillarIconName,
} from "shared/components/icons/pillarIconNames";
import PillarFormCombobox, {
  type ComboboxOptionProps,
  type PillarFormComboboxOption,
} from "shared/components/pillar-form/components/PillarFormCombobox";

export type IconOption = { id: string; name: PillarIconName; icon: ReactNode };

/**
 * Picker options for a set of registered icon names. Defaults to every icon in
 * PILLAR_ICON_DESCRIPTIONS; pass a narrower list to offer fewer.
 */
export const buildPillarIconOptions = (
  iconNames: readonly PillarIconName[] = PILLAR_ICON_NAMES,
): IconOption[] =>
  iconNames.map((iconName) => ({
    id: iconName,
    name: iconName,
    icon: (
      // Icons default to different sizes; pin one so every row lines up. The
      // color comes from the option's text color.
      <PillarIcon
        iconName={iconName}
        className="ion-like-icon h-3 w-3 shrink-0"
      />
    ),
  }));

//Changed export type ExtractProps<T> = T extends ComponentType<infer P> ? P : T;
//To ComponentPropsWithoutRef
//Maybe should use ComponentProps instead
//Or leave it as is
export type PillarFormIconPickerProps = Omit<
  ComponentPropsWithoutRef<typeof Combobox>,
  "value" | "id" | "refName" | "as" | "multiple" | "onChange"
> & {
  name: string;
  additionalClasses?: string;
  /** The options to offer; defaults to every registered icon. */
  iconsList?: IconOption[];
  label: string;
  placeholder?: string;
  testid?: string;
};

const IconOptionElement = ({ option }: ComboboxOptionProps<IconOption>) => {
  return (
    <Combobox.Option
      value={option.value}
      className="cursor-pointer rounded hover:bg-society-50 p-1 flex items-center text-neutral-mid-850"
    >
      {option.rawData?.icon || ""}
      <span className="ml-1 text-neutral-mid-850">{option.display}</span>
    </Combobox.Option>
  );
};

const PillarFormIconPicker = ({
  name,
  iconsList = buildPillarIconOptions(),
  label,
  additionalClasses,
  placeholder,
  testid,
}: PillarFormIconPickerProps) => {
  const staticIconOptionsList: PillarFormComboboxOption<IconOption>[] =
    iconsList.map((icon: IconOption) => ({
      key: `${icon.id}`,
      display: icon.name,
      value: icon.id,
      rawData: icon,
    }));

  return (
    <PillarFormCombobox<IconOption>
      name={name}
      label={label}
      testidInput={`form-icon-picker-${testid}-input`}
      comboOptionQuery={staticIconOptionsList}
      OptionComponent={IconOptionElement}
      multiple={false}
      placeholder={placeholder}
      additionalClasses={additionalClasses}
      nullable={true}
    />
  );
};

export default PillarFormIconPicker;
